import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Switch from "@mui/material/Switch";
import { useAuth } from "context/AuthContext";
import chat from "assets/images/illustrations/chat.png";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import AuthenticationLayout from "layouts/authenticationLayout/index";
import { getTokenContext, isAirportEntityType } from "utils/tokenUtils";

function getPostAuthPath(user, fallbackPath) {
  const entityType =
    user?.id_token != null
      ? getTokenContext(user.id_token).entityType
      : null;
  if (isAirportEntityType(entityType)) {
    return "/dashboard/pricing";
  }
  if (fallbackPath?.includes("/dashboard")) {
    return fallbackPath;
  }
  return "/dashboard";
}

function SignIn() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get the page user was trying to access before login
  const from = location.state?.from?.pathname || "/dashboard";
  const successMessage = location.state?.message || null;

  // Redirect if already signed in or continuing as guest
  useEffect(() => {
    if (auth.isGuest) {
      const redirectPath = from.includes("/dashboard") ? from : "/dashboard";
      navigate(redirectPath, { replace: true });
      return;
    }
    if (auth.isAuthenticated && auth.user) {
      navigate(getPostAuthPath(auth.user, from), { replace: true });
    }
  }, [auth.isGuest, auth.isAuthenticated, auth.user, navigate, from]);

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await auth.signIn(email.trim(), password);
      // Check if sign-in was successful
      if (result) {
        navigate(getPostAuthPath(result, from), { replace: true });
      }
    } catch (err) {
      console.error("Sign in error:", err);
      let errorMessage = "Sign in failed. Please try again.";

      if (err && err.code === "NotAuthorizedException") {
        errorMessage = "Incorrect email or password.";
      } else if (err && err.code === "UserNotConfirmedException") {
        errorMessage = "Please verify your email address before signing in.";
      } else if (err && err.code === "UserNotFoundException") {
        errorMessage = "User not found. Please check your email address.";
      } else if (err && err.code === "NEW_PASSWORD_REQUIRED") {
        // Redirect to set new password page
        navigate("/set-new-password", {
          state: { email: email.trim(), userAttributes: err.userAttributes },
          replace: true,
        });
        return; // Don't set error, we're redirecting
      } else if (err && err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const handleButtonClick = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Validate before submission
    if (!email.trim() || !password.trim()) {
      setError("Please enter both email and password.");
      return;
    }

    // If already submitting, prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    // Directly call handleSubmit
    await handleSubmit({ preventDefault: () => {} });
  };

  const handleContinueAsGuest = () => {
    setError(null);
    auth.signInAsGuest();
    navigate("/dashboard", { replace: true });
  };

  // Show loading state (initial check)
  if (auth.isLoading && !auth.user && !auth.isGuest) {
    return (
      <AuthenticationLayout
        title="Sign In"
        description="Loading..."
        illustration={{
          image: chat,
          title: '"Attention is the new currency"',
          description:
            "The more effortless the writing looks, the more effort the writer actually put into the process.",
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <SoftTypography variant="body1" color="text">
            Loading...
          </SoftTypography>
        </SoftBox>
      </AuthenticationLayout>
    );
  }

  return (
    <AuthenticationLayout
      title="Sign In"
      description="Enter your email and password to sign in"
      illustration={{
        image: chat,
        title: '"Attention is the new currency"',
        description:
          "The more effortless the writing looks, the more effort the writer actually put into the process.",
      }}
    >
      <SoftBox component="form" role="form" onSubmit={handleSubmit} noValidate>
        {successMessage && (
          <SoftBox mb={2} p={2} bgColor="success" borderRadius="lg">
            <SoftTypography variant="body2" color="white">
              {successMessage}
            </SoftTypography>
          </SoftBox>
        )}

        {error && (
          <SoftBox mb={2} p={2} bgColor="error" borderRadius="lg">
            <SoftTypography variant="body2" color="white">
              {error}
            </SoftTypography>
          </SoftBox>
        )}

        <SoftBox mb={2}>
          <SoftInput
            type="email"
            placeholder="Email"
            size="large"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isSubmitting}
            name="email"
            autoComplete="email"
          />
        </SoftBox>

        <SoftBox mb={2}>
          <SoftInput
            type="password"
            placeholder="Password"
            size="large"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isSubmitting}
            name="password"
            autoComplete="current-password"
          />
        </SoftBox>

        <SoftBox
          mt={1}
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <SoftBox display="flex" alignItems="center">
            <Switch
              checked={rememberMe}
              onChange={handleSetRememberMe}
              size="small"
            />
            <SoftTypography
              variant="button"
              fontWeight="regular"
              onClick={handleSetRememberMe}
              sx={{ cursor: "pointer", userSelect: "none" }}
            >
              &nbsp;&nbsp;Remember me
            </SoftTypography>
          </SoftBox>
          <SoftTypography
            component={Link}
            to="/reset-password"
            variant="button"
            color="info"
            fontWeight="medium"
          >
            Forgot password?
          </SoftTypography>
        </SoftBox>

        <SoftBox mt={4} mb={1}>
          <SoftButton
            type="button"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={isSubmitting || !email.trim() || !password.trim()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleButtonClick(e);
            }}
          >
            {isSubmitting ? "Signing in..." : "Sign in"}
          </SoftButton>
        </SoftBox>

        <SoftBox mt={2} mb={1}>
          <SoftButton
            type="button"
            variant="outlined"
            color="secondary"
            size="large"
            fullWidth
            disabled={isSubmitting}
            onClick={handleContinueAsGuest}
          >
            Continue as guest
          </SoftButton>
        </SoftBox>
        <SoftBox mb={2} textAlign="center">
          <SoftTypography variant="caption" color="secondary">
            Guest mode uses sample data only. Sign in for full access.
          </SoftTypography>
        </SoftBox>

        <SoftBox mt={3} textAlign="center">
          <SoftTypography variant="button" color="text" fontWeight="regular">
            Don&apos;t have an account?{" "}
            <SoftTypography
              component={Link}
              to="/sign-up"
              variant="button"
              color="secondary"
              fontWeight="medium"
            >
              Sign up
            </SoftTypography>
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    </AuthenticationLayout>
  );
}

export default SignIn;
